import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar } from '@material-ui/core';
import styled from 'styled-components';

import coreAPI from '../../apis/core';
import Switch from '../Shared/Switch';
import { HealtheraButton } from '../../healtheraComponents';
import DynamicDialog from '../DynamicDialog/DynamicDialog';
import { updateOrganisation, getOrganisation, assertSucceeded } from '../../redux/organisations';
import { planFeatureFlags } from '../OrganisationPlan/planFeatureFlags';
import { mergeFeatureFlagCatalogue } from './featureFlagCatalogue';

const sortAlphabetically = (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

export default function FeatureFlags(props) {
  const dispatch = useDispatch();
  const organisation = useSelector(({ organisations }) => organisations.organisation);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [planFlagKeys, setPlanFlagKeys] = useState(null);
  const [planFlagsError, setPlanFlagsError] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingFlagChange, setPendingFlagChange] = useState(null);

  const organisationId = organisation?._id;

  useEffect(() => {
    (async () => {
      try {
        const data = await coreAPI.getFeatureFlags();
        setFeatureFlags(data);
      } catch (error) {
        setError(error.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!organisationId) return;
    (async () => {
      try {
        const organisationFeatures = await coreAPI.getOrganisationFeatures(organisationId);
        setPlanFlagKeys(planFeatureFlags(organisationFeatures).map(flag => flag.key));
      } catch (error) {
        setPlanFlagsError(`Could not load the plan features: ${error.message}`);
      }
    })();
  }, [organisationId]);

  const mergedFeatureFlags = useMemo(() => mergeFeatureFlagCatalogue(featureFlags), [featureFlags]);

  const isPlanFlag = useCallback(flagId => !!planFlagKeys?.includes(flagId), [planFlagKeys]);

  const isFeatureFlagEnabled = flagId => {
    const orgValue = organisation.feature_flags?.[flagId];
    if (typeof orgValue === 'boolean') {
      return orgValue;
    }
    const flag = mergedFeatureFlags.find(f => f._id === flagId);
    return flag?.default_value ?? true;
  };

  const regularFeatureFlags = useMemo(
    () =>
      mergedFeatureFlags.filter(featureFlag => !featureFlag.pro && !isPlanFlag(featureFlag._id)).sort(sortAlphabetically),
    [mergedFeatureFlags, isPlanFlag]
  );

  const proFeatureFlags = useMemo(
    () =>
      mergedFeatureFlags.filter(featureFlag => featureFlag.pro && !isPlanFlag(featureFlag._id)).sort(sortAlphabetically),
    [mergedFeatureFlags, isPlanFlag]
  );

  const onFeatureFlagToggle = (flagId, flagTitle, newValue) => {
    setPendingFlagChange({ flagId, flagTitle, newValue });
  };

  const confirmFeatureFlagChange = async () => {
    if (!pendingFlagChange) return;
    const { flagId, flagTitle, newValue } = pendingFlagChange;
    try {
      assertSucceeded(await dispatch(updateOrganisation(organisation._id, { feature_flags: { [flagId]: newValue } })));
      await dispatch(getOrganisation(organisation._id));
      setSuccessMessage(`"${flagTitle}" has been ${newValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError('Failed to update feature flag');
    }
    setPendingFlagChange(null);
  };

  const cancelFeatureFlagChange = () => {
    setPendingFlagChange(null);
  };

  if (!organisation) {
    return null;
  }

  return (
    <div>
      <Snackbar
        open={!!error}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        message={error}
      />
      <Snackbar
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        message={successMessage}
      />
      <DynamicDialog
        open={!!pendingFlagChange}
        title={`Are you sure you want to ${pendingFlagChange?.newValue ? 'enable' : 'disable'} "${
          pendingFlagChange?.flagTitle
        }"?`}
        actions={
          <>
            <HealtheraButton onClick={confirmFeatureFlagChange} margin={'0 10px 5px'} height={'32px'} width={'95px'}>
              Confirm
            </HealtheraButton>
            <HealtheraButton onClick={cancelFeatureFlagChange} margin={'0 10px 5px'} height={'32px'} width={'95px'}>
              Cancel
            </HealtheraButton>
          </>
        }
      />
      <h1 style={{ textAlign: 'center' }}>{organisation.name}</h1>
      {planFlagsError && <Notice>{planFlagsError} — plan features are shown as plain switches</Notice>}
      {!planFlagsError && !planFlagKeys && <Notice>Loading the plan features…</Notice>}
      {(planFlagKeys || planFlagsError) && (
        <Container>
          <Column>
            <OutlinedContainer>
              <Header>Feature Flags</Header>
              {regularFeatureFlags.length > 0 && (
                <OptionsList>
                  {regularFeatureFlags.map(featureFlag => (
                    <Switch
                      key={featureFlag._id}
                      label={featureFlag.title}
                      onChange={(_, value) => onFeatureFlagToggle(featureFlag._id, featureFlag.title, value)}
                      checked={isFeatureFlagEnabled(featureFlag._id)}
                    />
                  ))}
                </OptionsList>
              )}
            </OutlinedContainer>
          </Column>
          <Column>
            {proFeatureFlags.length > 0 && (
              <OutlinedContainer>
                <Header>Pro Feature Flags</Header>
                {organisation.pro?.enabled !== true && <Hint>Turn on Healthera Pro in the Plan tab</Hint>}
                <OptionsList>
                  {proFeatureFlags.map(featureFlag => (
                    <Switch
                      key={featureFlag._id}
                      label={featureFlag.title}
                      onChange={(_, value) => onFeatureFlagToggle(featureFlag._id, featureFlag.title, value)}
                      checked={isFeatureFlagEnabled(featureFlag._id)}
                      disabled={organisation.pro?.enabled !== true}
                    />
                  ))}
                </OptionsList>
              </OutlinedContainer>
            )}
          </Column>
        </Container>
      )}
    </div>
  );
}

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > div {
    min-width: 300px;
  }
`;

const Container = styled.div`
  margin: 2rem 0;
  display: grid;
  grid-template-columns: 50% 50%;
  ${Column}:last-child {
    border-left: 1px solid rgba(0, 0, 0, 0.5);
  }
`;

const OutlinedContainer = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
`;

const Header = styled.div`
  line-height: 48px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.5);
  padding-left: 10px;
`;

const OptionsList = styled.div`
  padding-left: 10px;
`;

const Notice = styled.p`
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
`;

const Hint = styled.p`
  margin: 8px 10px 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
`;
